import { useState, useEffect } from 'react';
import { TopBar } from '../components/layout/TopBar';
import { promoCodesAPI, campaignsAPI, customersAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  Plus,
  Ticket,
  Megaphone,
  Send,
  Trash2,
  Tag,
  Percent,
  DollarSign,
  Calendar,
  Users,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  RefreshCw,
  Mail,
  MessageSquare,
  Copy
} from 'lucide-react';

const TAGS_OPTIONS = ['VIP', 'Frecuente', 'Nuevo', 'Corporativo', 'Cumpleaños'];

export default function Marketing() {
  const [activeTab, setActiveTab] = useState('promos');
  const [promoCodes, setPromoCodes] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPromoDialog, setShowPromoDialog] = useState(false);
  const [showCampaignDialog, setShowCampaignDialog] = useState(false);
  const [customerStats, setCustomerStats] = useState(null);

  const [promoForm, setPromoForm] = useState({
    code: '',
    description: '',
    discount_type: 'percentage',
    discount_value: 10,
    min_party_size: 1,
    max_uses: null,
    valid_from: new Date().toISOString().split('T')[0],
    valid_until: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  });

  const [campaignForm, setCampaignForm] = useState({
    name: '',
    campaign_type: 'sms',
    message_template: '',
    target_tags: [],
    target_all: false,
    promo_code_id: null,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [promoRes, campaignRes, statsRes] = await Promise.all([
        promoCodesAPI.list(),
        campaignsAPI.list(),
        customersAPI.getStats()
      ]);
      setPromoCodes(promoRes.data);
      setCampaigns(campaignRes.data);
      setCustomerStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePromo = async () => {
    try {
      if (!promoForm.code) {
        toast.error('El código es requerido');
        return;
      }
      await promoCodesAPI.create(promoForm);
      toast.success('Código promocional creado');
      setShowPromoDialog(false);
      setPromoForm({
        code: '',
        description: '',
        discount_type: 'percentage',
        discount_value: 10,
        min_party_size: 1,
        max_uses: null,
        valid_from: new Date().toISOString().split('T')[0],
        valid_until: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear código');
    }
  };

  const handleDeletePromo = async (id) => {
    try {
      await promoCodesAPI.delete(id);
      toast.success('Código eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleCreateCampaign = async () => {
    try {
      if (!campaignForm.name || !campaignForm.message_template) {
        toast.error('Nombre y mensaje son requeridos');
        return;
      }
      await campaignsAPI.create(campaignForm);
      toast.success('Campaña creada');
      setShowCampaignDialog(false);
      setCampaignForm({
        name: '',
        campaign_type: 'sms',
        message_template: '',
        target_tags: [],
        target_all: false,
        promo_code_id: null,
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear campaña');
    }
  };

  const handleSendCampaign = async (campaignId) => {
    try {
      const res = await campaignsAPI.send(campaignId);
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar campaña');
    }
  };

  const handleDeleteCampaign = async (id) => {
    try {
      await campaignsAPI.delete(id);
      toast.success('Campaña eliminada');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Código copiado');
  };

  const toggleTag = (tag) => {
    if (campaignForm.target_tags.includes(tag)) {
      setCampaignForm({
        ...campaignForm,
        target_tags: campaignForm.target_tags.filter(t => t !== tag)
      });
    } else {
      setCampaignForm({
        ...campaignForm,
        target_tags: [...campaignForm.target_tags, tag]
      });
    }
  };

  const campaignStatusColors = {
    draft: 'bg-gray-500',
    scheduled: 'bg-blue-500',
    sending: 'bg-yellow-500',
    completed: 'bg-green-500',
    failed: 'bg-red-500',
  };

  const campaignStatusLabels = {
    draft: 'Borrador',
    scheduled: 'Programada',
    sending: 'Enviando',
    completed: 'Completada',
    failed: 'Fallida',
  };

  return (
    <div className="min-h-screen" data-testid="marketing-page">
      <TopBar
        title="Marketing"
        helpSection="marketing"
        actions={
          <Button
            size="sm"
            variant="outline"
            onClick={fetchData}
            className="rounded-xl"
            data-testid="refresh-marketing-btn"
          >
            <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
          </Button>
        }
      />

      <div className="p-6">
        {/* Stats */}
        {customerStats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card className="border-0 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-xl bg-primary/10">
                    <Users className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{customerStats.total_customers}</p>
                    <p className="text-xs text-muted-foreground">Total clientes</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-xl bg-yellow-500/10">
                    <Tag className="h-5 w-5 text-yellow-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{customerStats.vip_count}</p>
                    <p className="text-xs text-muted-foreground">Clientes VIP</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-xl bg-green-500/10">
                    <Ticket className="h-5 w-5 text-green-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{promoCodes.length}</p>
                    <p className="text-xs text-muted-foreground">Códigos activos</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-xl bg-blue-500/10">
                    <Megaphone className="h-5 w-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{campaigns.length}</p>
                    <p className="text-xs text-muted-foreground">Campañas</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="promos" className="flex items-center gap-2">
              <Ticket className="h-4 w-4" /> Códigos Promocionales
            </TabsTrigger>
            <TabsTrigger value="campaigns" className="flex items-center gap-2">
              <Megaphone className="h-4 w-4" /> Campañas
            </TabsTrigger>
          </TabsList>

          {/* Promo Codes Tab */}
          <TabsContent value="promos">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Códigos Promocionales</h2>
              <Button onClick={() => setShowPromoDialog(true)} className="rounded-xl" data-testid="new-promo-btn">
                <Plus className="h-4 w-4 mr-1" /> Nuevo Código
              </Button>
            </div>

            <Card className="border-0 shadow-sm">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Código</TableHead>
                    <TableHead>Descuento</TableHead>
                    <TableHead>Validez</TableHead>
                    <TableHead className="text-center">Usos</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {promoCodes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        No hay códigos promocionales
                      </TableCell>
                    </TableRow>
                  ) : (
                    promoCodes.map((promo) => (
                      <TableRow key={promo.id} data-testid={`promo-row-${promo.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <code className="px-2 py-1 bg-primary/10 rounded-lg font-mono font-bold text-primary">
                              {promo.code}
                            </code>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => copyCode(promo.code)}
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                          {promo.description && (
                            <p className="text-xs text-muted-foreground mt-1">{promo.description}</p>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {promo.discount_type === 'percentage' ? (
                              <>
                                <Percent className="h-4 w-4 text-green-500" />
                                <span className="font-semibold">{promo.discount_value}%</span>
                              </>
                            ) : (
                              <>
                                <DollarSign className="h-4 w-4 text-green-500" />
                                <span className="font-semibold">${promo.discount_value}</span>
                              </>
                            )}
                          </div>
                          {promo.min_party_size > 1 && (
                            <p className="text-xs text-muted-foreground">
                              Mín. {promo.min_party_size} personas
                            </p>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <p>{promo.valid_from}</p>
                            <p className="text-muted-foreground">→ {promo.valid_until}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="font-semibold">{promo.uses_count}</span>
                          {promo.max_uses && (
                            <span className="text-muted-foreground"> / {promo.max_uses}</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge
                            className={cn(
                              promo.status === 'active' && 'bg-green-500',
                              promo.status === 'expired' && 'bg-red-500',
                              promo.status === 'disabled' && 'bg-gray-500'
                            )}
                          >
                            {promo.status === 'active' ? 'Activo' : promo.status === 'expired' ? 'Expirado' : 'Desactivado'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="rounded-xl text-destructive hover:text-destructive"
                            onClick={() => handleDeletePromo(promo.id)}
                            data-testid={`delete-promo-${promo.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          {/* Campaigns Tab */}
          <TabsContent value="campaigns">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Campañas de Marketing</h2>
              <Button onClick={() => setShowCampaignDialog(true)} className="rounded-xl" data-testid="new-campaign-btn">
                <Plus className="h-4 w-4 mr-1" /> Nueva Campaña
              </Button>
            </div>

            <div className="grid gap-4">
              {campaigns.length === 0 ? (
                <Card className="border-0 shadow-sm">
                  <CardContent className="py-12 text-center text-muted-foreground">
                    <Megaphone className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No hay campañas de marketing</p>
                  </CardContent>
                </Card>
              ) : (
                campaigns.map((campaign) => (
                  <Card key={campaign.id} className="border-0 shadow-sm" data-testid={`campaign-card-${campaign.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <div className={cn(
                              "p-2 rounded-xl",
                              campaign.campaign_type === 'sms' && "bg-blue-500/10",
                              campaign.campaign_type === 'email' && "bg-purple-500/10",
                              campaign.campaign_type === 'whatsapp' && "bg-green-500/10"
                            )}>
                              {campaign.campaign_type === 'sms' && <MessageSquare className="h-5 w-5 text-blue-500" />}
                              {campaign.campaign_type === 'email' && <Mail className="h-5 w-5 text-purple-500" />}
                              {campaign.campaign_type === 'whatsapp' && <MessageSquare className="h-5 w-5 text-green-500" />}
                            </div>
                            <div>
                              <h3 className="font-semibold">{campaign.name}</h3>
                              <p className="text-sm text-muted-foreground">
                                {campaign.campaign_type.toUpperCase()} · {campaign.recipients_count} destinatarios
                              </p>
                            </div>
                            <Badge className={campaignStatusColors[campaign.status]}>
                              {campaignStatusLabels[campaign.status]}
                            </Badge>
                          </div>

                          <div className="bg-muted p-3 rounded-xl text-sm mb-3">
                            "{campaign.message_template}"
                          </div>

                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            {campaign.target_all ? (
                              <span className="flex items-center gap-1">
                                <Users className="h-3 w-3" /> Todos los clientes
                              </span>
                            ) : (
                              <span className="flex items-center gap-1">
                                <Tag className="h-3 w-3" /> {campaign.target_tags?.join(', ') || 'Sin filtro'}
                              </span>
                            )}
                            {campaign.completed_at && (
                              <span className="flex items-center gap-1">
                                <CheckCircle className="h-3 w-3 text-green-500" />
                                {campaign.sent_count} enviados
                                {campaign.failed_count > 0 && (
                                  <span className="text-red-500">· {campaign.failed_count} fallidos</span>
                                )}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          {campaign.status === 'draft' && (
                            <Button
                              size="sm"
                              onClick={() => handleSendCampaign(campaign.id)}
                              className="rounded-xl"
                              data-testid={`send-campaign-${campaign.id}`}
                            >
                              <Send className="h-4 w-4 mr-1" /> Enviar
                            </Button>
                          )}
                          {campaign.status === 'sending' && (
                            <Button size="sm" disabled className="rounded-xl">
                              <Loader2 className="h-4 w-4 animate-spin mr-1" /> Enviando...
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="rounded-xl text-destructive hover:text-destructive"
                            onClick={() => handleDeleteCampaign(campaign.id)}
                            data-testid={`delete-campaign-${campaign.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Promo Dialog */}
      <Dialog open={showPromoDialog} onOpenChange={setShowPromoDialog}>
        <DialogContent className="rounded-2xl" data-testid="promo-dialog">
          <DialogHeader>
            <DialogTitle>Nuevo Código Promocional</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Código *</Label>
              <Input
                value={promoForm.code}
                onChange={(e) => setPromoForm({ ...promoForm, code: e.target.value.toUpperCase() })}
                placeholder="Ej: BIENVENIDO20"
                className="rounded-xl font-mono"
                data-testid="promo-code-input"
              />
            </div>

            <div>
              <Label>Descripción</Label>
              <Input
                value={promoForm.description}
                onChange={(e) => setPromoForm({ ...promoForm, description: e.target.value })}
                placeholder="Ej: 20% de descuento para nuevos clientes"
                className="rounded-xl"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tipo de descuento</Label>
                <Select
                  value={promoForm.discount_type}
                  onValueChange={(v) => setPromoForm({ ...promoForm, discount_type: v })}
                >
                  <SelectTrigger className="rounded-xl" data-testid="discount-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Porcentaje (%)</SelectItem>
                    <SelectItem value="fixed">Monto fijo ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Valor</Label>
                <Input
                  type="number"
                  value={promoForm.discount_value}
                  onChange={(e) => setPromoForm({ ...promoForm, discount_value: parseFloat(e.target.value) || 0 })}
                  className="rounded-xl"
                  data-testid="discount-value-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Válido desde</Label>
                <Input
                  type="date"
                  value={promoForm.valid_from}
                  onChange={(e) => setPromoForm({ ...promoForm, valid_from: e.target.value })}
                  className="rounded-xl"
                />
              </div>
              <div>
                <Label>Válido hasta</Label>
                <Input
                  type="date"
                  value={promoForm.valid_until}
                  onChange={(e) => setPromoForm({ ...promoForm, valid_until: e.target.value })}
                  className="rounded-xl"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Mín. personas</Label>
                <Input
                  type="number"
                  min={1}
                  value={promoForm.min_party_size}
                  onChange={(e) => setPromoForm({ ...promoForm, min_party_size: parseInt(e.target.value) || 1 })}
                  className="rounded-xl"
                />
              </div>
              <div>
                <Label>Máx. usos (vacío = ilimitado)</Label>
                <Input
                  type="number"
                  value={promoForm.max_uses || ''}
                  onChange={(e) => setPromoForm({ ...promoForm, max_uses: e.target.value ? parseInt(e.target.value) : null })}
                  className="rounded-xl"
                  placeholder="Ilimitado"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPromoDialog(false)} className="rounded-xl">
              Cancelar
            </Button>
            <Button onClick={handleCreatePromo} className="rounded-xl" data-testid="create-promo-btn">
              Crear Código
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Campaign Dialog */}
      <Dialog open={showCampaignDialog} onOpenChange={setShowCampaignDialog}>
        <DialogContent className="rounded-2xl max-w-lg" data-testid="campaign-dialog">
          <DialogHeader>
            <DialogTitle>Nueva Campaña de Marketing</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Nombre de la campaña *</Label>
              <Input
                value={campaignForm.name}
                onChange={(e) => setCampaignForm({ ...campaignForm, name: e.target.value })}
                placeholder="Ej: Promoción de fin de semana"
                className="rounded-xl"
                data-testid="campaign-name-input"
              />
            </div>

            <div>
              <Label>Canal de envío</Label>
              <Select
                value={campaignForm.campaign_type}
                onValueChange={(v) => setCampaignForm({ ...campaignForm, campaign_type: v })}
              >
                <SelectTrigger className="rounded-xl" data-testid="campaign-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sms">📱 SMS</SelectItem>
                  <SelectItem value="email">📧 Email</SelectItem>
                  <SelectItem value="whatsapp" disabled>💬 WhatsApp (próximamente)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Mensaje *</Label>
              <Textarea
                value={campaignForm.message_template}
                onChange={(e) => setCampaignForm({ ...campaignForm, message_template: e.target.value })}
                placeholder="Usa {nombre} para personalizar y {restaurante} para el nombre del restaurante"
                rows={4}
                className="rounded-xl"
                data-testid="campaign-message-textarea"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Variables: {'{nombre}'}, {'{restaurante}'}
              </p>
            </div>

            <div>
              <Label className="mb-2 block">Destinatarios</Label>
              <div className="flex items-center justify-between p-3 rounded-xl bg-muted mb-3">
                <span>Enviar a todos los clientes</span>
                <Switch
                  checked={campaignForm.target_all}
                  onCheckedChange={(v) => setCampaignForm({ ...campaignForm, target_all: v, target_tags: [] })}
                />
              </div>

              {!campaignForm.target_all && (
                <div>
                  <p className="text-sm mb-2">O filtrar por etiquetas:</p>
                  <div className="flex flex-wrap gap-2">
                    {TAGS_OPTIONS.map((tag) => (
                      <Badge
                        key={tag}
                        variant={campaignForm.target_tags.includes(tag) ? 'default' : 'outline'}
                        className="cursor-pointer"
                        onClick={() => toggleTag(tag)}
                      >
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {promoCodes.length > 0 && (
              <div>
                <Label>Incluir código promocional (opcional)</Label>
                <Select
                  value={campaignForm.promo_code_id || 'none'}
                  onValueChange={(v) => setCampaignForm({ ...campaignForm, promo_code_id: v === 'none' ? null : v })}
                >
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder="Sin código" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin código promocional</SelectItem>
                    {promoCodes.map((promo) => (
                      <SelectItem key={promo.id} value={promo.id}>
                        {promo.code} - {promo.discount_type === 'percentage' ? `${promo.discount_value}%` : `$${promo.discount_value}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCampaignDialog(false)} className="rounded-xl">
              Cancelar
            </Button>
            <Button onClick={handleCreateCampaign} className="rounded-xl" data-testid="create-campaign-btn">
              Crear Campaña
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
